import { useGlobal } from "@/app/providers/use-global";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import Icon from "@/components/ui/icon";
import { useScreen } from "@/hooks/use-screen";
import type { PromptAndResponse } from "@/hooks/use-send-message";
import { type ReactNode, memo, useMemo } from "react";
import type { ChatStats } from "./models";

interface StatsPopupProps {
  chat: PromptAndResponse[];
}

const StatsPopup = memo(({ chat }: StatsPopupProps) => {
  const { selectedPrompt } = useGlobal();
  const { height } = useScreen();

  const isShortScreen = useMemo(() => height < 890, [height]);

  const chatStats: ChatStats | null = useMemo(() => {
    if (chat.length === 0) {
      return null;
    }
    return {
      responseStats: selectedPrompt
        ? chat[chat.length - 1].edited_stats
        : chat[chat.length - 1].stats,
      responseDuration: selectedPrompt
        ? chat[chat.length - 1].edited_response_duration
        : chat[chat.length - 1].response_duration,
      model: chat[chat.length - 1].model,
    };
  }, [chat]);

  const triggerContent = useMemo<ReactNode>(() => {
    if (chat.length <= 0) {
      return null;
    }
    if (selectedPrompt && !chat[chat.length - 1].edited_stats) {
      return null;
    }
    if (!(selectedPrompt || chat[chat.length - 1].stats)) {
      return null;
    }

    return (
      <div className="flex items-center space-x-2">
        <div className="text-end text-3xl group-[.iframe]:text-2xl">
          {(selectedPrompt
            ? (chat[chat.length - 1].edited_stats?.completion_tokens ?? 1) /
              (chat[chat.length - 1].edited_stats?.completion_time ?? 1)
            : (chat[chat.length - 1].stats?.completion_tokens ?? 1) /
              (chat[chat.length - 1].stats?.completion_time ?? 1)
          ).toFixed(2)}{" "}
          T/s
        </div>
        <Icon
          className="fill-none dark:fill-groq-orange p-1"
          icon="zap"
          size="lg"
        />
      </div>
    );
  }, [chat]);

  const popupContent = useMemo<ReactNode>(
    () => (
      <>
        <h3 className="flex items-center justify-center font-semibold text-groq-orange mt-3 md:mt-[18px]">
          <Icon
            className="fill-none dark:fill-groq-orange"
            icon="zap"
            size="md"
          />
          <span className="ml-2">Speed Insights</span>
        </h3>

        <table className="leading-6 mt-4 w-full">
          <tbody>
            <tr>
              <td colSpan={3}>
                <p className="ml-6 mb-2 font-semibold">Tokens</p>
              </td>
            </tr>
            <tr>
              <td>
                <div className="flex flex-col items-center ml-4">
                  <p className="text-2xl font-light">
                    {chatStats?.responseStats?.prompt_tokens}
                  </p>
                  <p className="text-[11px] mt-[3px]">Input tokens</p>
                </div>
              </td>
              <td>
                <div className="flex flex-col items-center">
                  <p className="text-2xl font-light">
                    {chatStats?.responseStats?.completion_tokens}
                  </p>
                  <p className="text-[11px] mt-[3px]">Output tokens</p>
                </div>
              </td>
              <td>
                <div className="flex flex-col items-center mr-5">
                  <p className="text-[32px] leading-8">
                    {chatStats?.responseStats?.total_tokens}
                  </p>
                  <p className="text-[11px] font-semibold mt-[3px]">Tokens</p>
                </div>
              </td>
            </tr>

            <tr>
              <td colSpan={3}>
                <p className="ml-6 mt-3 mb-2 font-semibold">Inference Time</p>
              </td>
            </tr>
            <tr>
              <td>
                <div className="flex flex-col items-center mb-3 ml-4">
                  <p className="text-2xl font-light">
                    {chatStats?.responseStats?.prompt_time.toFixed(2)}
                  </p>
                  <p className="text-[11px] mt-[3px]">Input seconds</p>
                </div>
              </td>
              <td>
                <div className="flex flex-col items-center mb-3">
                  <p className="text-2xl font-light">
                    {chatStats?.responseStats?.completion_time.toFixed(2)}
                  </p>
                  <p className="text-[11px] mt-[3px]">Output seconds</p>
                </div>
              </td>
              <td>
                <div className="flex flex-col items-center mb-3 mr-5">
                  <p className="text-[32px] leading-8">
                    {chatStats?.responseStats?.total_time.toFixed(2)}
                  </p>
                  <p className="text-[11px] font-semibold mt-[3px]">Seconds</p>
                </div>
              </td>
            </tr>

            <tr className="bg-accent">
              <td colSpan={3}>
                <p className="ml-6 mt-4 mb-2 font-semibold">Tokens / second</p>
              </td>
            </tr>
            <tr className="bg-accent">
              <td>
                <div className="flex flex-col items-center mb-4 ml-4">
                  <p className="text-2xl font-light">
                    {chatStats?.responseStats &&
                      (
                        chatStats.responseStats.prompt_tokens /
                        chatStats.responseStats.prompt_time
                      ).toFixed(0)}
                  </p>
                  <p className="text-[11px] mt-[3px]">Input</p>
                </div>
              </td>
              <td>
                <div className="flex flex-col items-center mb-4">
                  <p className="text-2xl font-light">
                    {chatStats?.responseStats &&
                      (
                        chatStats.responseStats.completion_tokens /
                        chatStats.responseStats.completion_time
                      ).toFixed(0)}
                  </p>
                  <p className="text-[11px] mt-[3px]">Output</p>
                </div>
              </td>
              <td>
                <div className="flex flex-col items-center mb-4 mr-5">
                  <p className="text-[32px] leading-8">
                    {chatStats?.responseStats &&
                      (
                        chatStats.responseStats.total_tokens /
                        chatStats.responseStats.total_time
                      ).toFixed(0)}
                  </p>
                  <p className="text-[11px] font-semibold mt-[3px]">Total</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div className="flex items-center justify-between text-xs mb-5 mx-7 md:mx-8 mt-5 leading-[10px] gap-2">
          <p className="text-muted-text">
            Round trip time:{" "}
            <span className="text-black dark:text-white">
              {chatStats?.responseDuration}s
            </span>
          </p>
          <p className="text-muted-text truncate">
            Model:{" "}
            <span className="text-black dark:text-white">
              {chatStats?.model?.id}
            </span>
          </p>
        </div>
      </>
    ),
    [chatStats],
  );

  if (chat.length <= 0) {
    return null;
  }

  return (
    <>
      <Dialog>
        <DialogTrigger className="block lg:hidden">
          {triggerContent}
        </DialogTrigger>
        <DialogContent className="whitespace-nowrap dark:text-white dark:bg-shallow dark:border-mutedaccent p-0 w-fit rounded-[10px] gap-0">
          {popupContent}
        </DialogContent>
      </Dialog>

      <HoverCard openDelay={100} closeDelay={100}>
        <HoverCardTrigger className="hidden lg:block">
          {triggerContent}
        </HoverCardTrigger>
        <HoverCardContent
          className="min-w-[370px] whitespace-nowrap dark:text-white dark:bg-shallow dark:border-mutedaccent p-0"
          hideWhenDetached={true}
          align={isShortScreen ? "start" : "end"}
          alignOffset={isShortScreen ? -110 : 0}
          side={isShortScreen ? "left" : "bottom"}
          sideOffset={isShortScreen ? 10 : 0}
        >
          {popupContent}
        </HoverCardContent>
      </HoverCard>
    </>
  );
});

StatsPopup.displayName = "StatsPopup";

export default StatsPopup;
